import { format } from "date-fns";

import {
  toApiDatetimeString,
  toLocalDatetimeInput,
} from "../AnnouncementManagement/releaseNotesTransforms";

export const EMPTY_DISPLAY = "未設定";
export const GOOGLE_DRIVE_DOWNLOAD_BASE =
  "https://drive.google.com/uc?export=download&id=";

const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const next = String(value).trim();
    if (next) return next;
  }
  return "";
};

const toBooleanish = (value, fallback = false) => {
  if (value == null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
};

const toValidDate = (value) => {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const normalized =
    typeof value === "string" && value.includes(" ") && !value.includes("T")
      ? value.replace(" ", "T")
      : value;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

export const formatUploadedAt = (value) => {
  const d = toValidDate(value);
  if (!d) return EMPTY_DISPLAY;
  return format(d, "yyyy-MM-dd HH:mm");
};

export const formatUploadedDateOnly = (value) => {
  const d = toValidDate(value);
  if (!d) return EMPTY_DISPLAY;
  return format(d, "yyyy.MM.dd");
};

export const normalizeAiLearningResourceListRow = (row) => {
  const imageUrl = pickFirstNonEmpty(
    row?.cover_image?.url,
    row?.image_url,
    row?.image,
    row?.cover_image_url,
    row?.thumbnail_url,
    row?.meta_json?.image_url,
  );
  const uploadedAt =
    row?.publish_at ??
    row?.uploaded_at ??
    row?.upload_time ??
    row?.created_at ??
    row?.published_at ??
    null;
  const googleDriveUrl = pickFirstNonEmpty(
    row?.google_drive_url,
    row?.download_url,
    row?.url_path,
    row?.url,
    row?.file_url,
    row?.meta_json?.download_url,
  );

  return {
    id: row?.id,
    title: row?.title ?? "",
    imageUrl,
    uploadedAt,
    isPublished: toBooleanish(row?.display ?? row?.is_published, false),
    downloadCount: Number(
      row?.download_count ?? row?.downloads_count ?? row?.downloads ?? 0,
    ),
    googleDriveUrl,
    youtubeUrl: pickFirstNonEmpty(row?.youtube_url),
    facebookUrl: pickFirstNonEmpty(row?.facebook_url),
    instagramUrl: pickFirstNonEmpty(row?.instagram_url),
  };
};

export const mapAiLearningResourceDetailToFormValues = (data) => ({
  title: data?.title ?? "",
  coverImageUrl: pickFirstNonEmpty(
    data?.cover_image?.url,
    data?.image_url,
    data?.image,
    data?.cover_image_url,
    data?.thumbnail_url,
    data?.meta_json?.image_url,
  ),
  uploadedAt: toLocalDatetimeInput(
    data?.publish_at ??
    data?.uploaded_at ??
      data?.upload_time ??
      data?.created_at ??
      data?.published_at ??
      "",
  ),
  googleDriveFileId: extractGoogleDriveFileId(
    data?.google_drive_url,
  ),
  youtubeUrl: pickFirstNonEmpty(data?.youtube_url),
  facebookUrl: pickFirstNonEmpty(data?.facebook_url),
  instagramUrl: pickFirstNonEmpty(data?.instagram_url),
  display: toBooleanish(data?.display, true),
});

function extractGoogleDriveFileId(...values) {
  const urlRaw = pickFirstNonEmpty(...values);
  if (!urlRaw) return "";

  try {
    const url = new URL(urlRaw);
    const idFromQuery = url.searchParams.get("id");
    if (idFromQuery) return idFromQuery.trim();

    const match = url.pathname.match(/\/d\/([a-zA-Z0-9_-]{10,})/);
    if (match?.[1]) return match[1];
  } catch {
    const queryMatch = String(urlRaw).match(/[?&]id=([a-zA-Z0-9_-]{10,})/);
    if (queryMatch?.[1]) return queryMatch[1];
  }

  return "";
}

export const buildAiLearningResourcePayload = ({
  title,
  uploadedAtLocal,
  googleDriveFileId,
  youtubeUrl,
  facebookUrl,
  instagramUrl,
  display,
}) => {
  const uploadedAt = toApiDatetimeString(uploadedAtLocal);

  return {
    title: String(title || "").trim(),
    publish_at: uploadedAt ?? null,
    google_drive_url: `${GOOGLE_DRIVE_DOWNLOAD_BASE}${String(
      googleDriveFileId || "",
    ).trim()}`,
    youtube_url: String(youtubeUrl || "").trim(),
    facebook_url: String(facebookUrl || "").trim(),
    instagram_url: String(instagramUrl || "").trim(),
    display: Boolean(display),
  };
};

export const buildAiLearningResourceMultipartPayload = ({
  title,
  uploadedAtLocal,
  googleDriveFileId,
  youtubeUrl,
  facebookUrl,
  instagramUrl,
  display,
  coverImageFile,
}) => {
  const formData = new FormData();
  formData.append("title", String(title || "").trim());
  formData.append(
    "google_drive_url",
    `${GOOGLE_DRIVE_DOWNLOAD_BASE}${String(googleDriveFileId || "").trim()}`,
  );

  const publishAt = toApiDatetimeString(uploadedAtLocal);
  if (publishAt) {
    formData.append("publish_at", publishAt);
  }

  const youtube = String(youtubeUrl || "").trim();
  const facebook = String(facebookUrl || "").trim();
  const instagram = String(instagramUrl || "").trim();
  if (youtube) formData.append("youtube_url", youtube);
  if (facebook) formData.append("facebook_url", facebook);
  if (instagram) formData.append("instagram_url", instagram);
  formData.append("display", display ? "1" : "0");

  if (coverImageFile instanceof File) {
    formData.append("cover_image", coverImageFile);
  }

  return formData;
};
